import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import * as mobxReact from 'mobx-react';
import * as IoC from 'typescript-ioc';
import { isSameDay } from 'date-fns';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { DateTimeFormatter } from '../../../infrastructure/implementations/date-formatter';
import { DateFnsFormats } from '../../../infrastructure/date-formatter';
import './date-multi-selection-dropdown.scss';
import { utcToZonedTime } from '../../../infrastructure/utils/date-fns-overrides.utils';
import Text from '../text/text';
import { getHourStringInEventTimezone } from '../../../infrastructure/utils/date.util';
import { MultiSelectOption } from '../multi-select-list/_multi-select-option';
import DefaultButton from '../default-button/default-button';
import { meetingsSortOrdersLabels } from '../../../constants/meeting.constants';
import { OERadioSingleOption } from '../oe-radio-options/oe-radio-single-option';
import Arrow from '../../../images/svg/icons8-expand-arrow';
import { SortOrder } from '../../../api/shared.dto';
import { LOADING } from '../../../constants/common-labels.constants';

export interface IProps {
    eventTimezone: string;
    fetchHours: () => void;
    filterHoursList: Date[] | null;
    selectedHours: Date[];
    intl: IntlShape;
    onApply: (hours: Date[], sortOrder: SortOrder) => void;
    selectedSortOrder: SortOrder;
}

const DateMultiSelectionDropdown = (props: IProps) => {
    const { eventTimezone, filterHoursList, fetchHours, selectedHours, onApply, intl, selectedSortOrder } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [tempHours, setTempHours] = useState<Date[]>(selectedHours);
    const [sortOpt, setSortOpt] = useState<SortOrder>(selectedSortOrder);
    const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);
    let dayGroups: Date[][];

    useEffect(() => {
        setSortOpt(selectedSortOrder);
    }, [selectedSortOrder]);

    const onDropdownClick = () => {
        if (!isOpen) fetchHours();
        setIsOpen(!isOpen);
        setTempHours(selectedHours);
        setSortOpt(selectedSortOrder);
    };

    const handleSelectDay = (dayHours: Date[], isSelected: boolean) => {
        if (isSelected) {
            setTempHours(prev => prev.filter(hour => !dayHours.find(el => el.toString() === hour.toString())));
        } else {
            setTempHours(prev => [...prev, ...dayHours]);
        }
    };

    const handleSelectHour = (hour: Date, isSelected: boolean) => {
        if (isSelected) {
            setTempHours(prev => prev.filter(el => el.toString() !== hour.toString()));
        } else {
            setTempHours(prev => [...prev, hour]);
        }
    };

    const handleReset = () => {
        setTempHours([]);
        setSortOpt(SortOrder.Asc);
    };

    const handleApply = () => {
        setIsOpen(false);
        onApply(tempHours, sortOpt);
    };

    const getDayString = (date: Date) => dateTimeFormatter.dateToFormattedString(
        date,
        DateFnsFormats.StringifiedShortDay,
    );

    const getDayGroupsFromHours = () => {
        let prevHour;
        const result: Date[][] = [];

        filterHoursList.forEach(hour => {
            if (prevHour) {
                const zonedPrevHour = utcToZonedTime(prevHour, eventTimezone);
                const zonedCurrentHour = utcToZonedTime(hour, eventTimezone);

                if (isSameDay(zonedPrevHour, zonedCurrentHour)) {
                    result[result.length - 1].push(hour);
                } else {
                    result.push([hour]);
                }
            } else {
                result.push([hour]);
            }
            prevHour = hour;
        });

        return result;
    };

    const getIsHourSelected = (hour: Date): boolean => {
        return !!tempHours.find(el => el.toString() === hour.toString());
    };

    const getIsDaySelected = (dayHours: Date[]): boolean => {
        return dayHours.every(hour => getIsHourSelected(hour));
    };

    if (filterHoursList) {
        dayGroups = getDayGroupsFromHours();
    }

    const sortOptionsLabels = meetingsSortOrdersLabels(intl);

    return (
        <div className='date-selection-dropdown multi-selection-dropdown'>
            <div
                onClick={onDropdownClick}
                className={classnames('cell-header-btn', {
                    'cell-header-btn--active': isOpen || selectedHours.length
                })}
            >
                <Text message={intl.formatMessage({
                        id: 'oe.common.dateAndTime',
                        description: 'Label text',
                        defaultMessage: 'Date and Time'
                    })}
                    withEllipsis
                />
                <div className={`${selectedSortOrder} multi-selection-dropdown__sort-direction`}>
                    <Arrow />
                </div>
            </div>

            {isOpen &&
            <>
                <div className='multi-selection-dropdown__overlay' onClick={() => setIsOpen(false)}/>
                <div className='date-selection-dropdown__day-groups multi-selection-dropdown__options'>
                    {dayGroups?.length ? (
                        <>
                        <div
                            className='multi-selection-dropdown__content-wrapper'
                        >
                            <div className='multi-selection-dropdown__sort'>
                                <div className='multi-selection-dropdown__section-title'>
                                    {
                                        intl.formatMessage({
                                            id: 'oe.meetings.sortMeetings',
                                            description: 'Label text',
                                            defaultMessage: 'Sort Meetings:'
                                        })
                                    }
                                </div>
                                <div className='multi-selection-dropdown__sort-items'>
                                    {
                                        Object.values(SortOrder).map(option => {
                                            return <div
                                                key={option}
                                                className={`multi-selection-dropdown__sort-item ${option}`}
                                            >
                                                <OERadioSingleOption
                                                    option={{ label: sortOptionsLabels[option] }}
                                                    value={selectedSortOrder}
                                                    checked={option === sortOpt}
                                                    onToggle={() => setSortOpt(option as SortOrder)}
                                                />
                                                <Arrow />
                                            </div>;
                                        })
                                    }
                                </div>

                            </div>
                            <div className='multi-selection-dropdown__list'>
                                <div className='multi-selection-dropdown__section-title'>
                                    {
                                        intl.formatMessage({
                                            id: 'oe.meetings.filterMeetings',
                                            description: 'Label text',
                                            defaultMessage: 'Filter Meetings:'
                                        })
                                    }
                                </div>
                                {
                                    dayGroups.map(group => {
                                        const [currentDay] = group;
                                        const currentDayInEventTimezone = DateTimeFormatter.applyTimeZoneToDate(currentDay, eventTimezone);
                                        const dayStringInEventTimezone = getDayString(currentDayInEventTimezone);

                                        return (
                                            <div className='date-selection-dropdown__day-group' key={currentDayInEventTimezone.toString()}>
                                                <div
                                                    className={classnames('date-selection-dropdown__day multi-select-list__option', {
                                                        'multi-select-list__option--selected': getIsDaySelected(group)
                                                    })}
                                                    onClick={() => handleSelectDay(group, getIsDaySelected(group))}
                                                >
                                                    {dayStringInEventTimezone}
                                                    <MultiSelectOption
                                                        isSelected={getIsDaySelected(group)}
                                                    />
                                                </div>
                                                <div className='date-selection-dropdown__day-group-hours multi-select__options'>
                                                    {group.map(hour => {
                                                        const timeStringInEventTimeZone = getHourStringInEventTimezone(
                                                            dateTimeFormatter, hour, eventTimezone
                                                        );
                                                        const isHourSelected = getIsHourSelected(hour);

                                                        return (
                                                            <div
                                                                className={classnames('date-selection-dropdown__hour multi-select-list__option', {
                                                                    'multi-select-list__option--selected': isHourSelected
                                                                })}
                                                                key={hour.toString()}
                                                                onClick={() => handleSelectHour(hour, isHourSelected)}
                                                            >
                                                                {timeStringInEventTimeZone}
                                                                <MultiSelectOption
                                                                    isSelected={isHourSelected}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>
                        <div className='multi-selection-dropdown__button-bar'>
                            <DefaultButton
                                modifier='cancel'
                                text='Reset'
                                onClick={handleReset}
                                className='read-only-textarea__controls-button'
                                disabled={!tempHours.length}
                            />
                            <DefaultButton
                                modifier='confirm-safe'
                                text='Apply'
                                onClick={handleApply}
                                className='read-only-textarea__controls-button'
                            />
                        </div>
                        </>
                        ) : (
                        <div className='date-selection-dropdown__placeholder'>
                            {dayGroups ? (
                                <FormattedMessage
                                    id='oe.control.noMeetingsFound'
                                    description='Placeholder for meetings filter popover'
                                    defaultMessage='No meetings found'
                                />
                            ) : (
                                <FormattedMessage
                                    {...LOADING}
                                    values={{
                                        suffix: '...'
                                    }}
                                />
                            )
                            }
                        </div>
                    )}
                </div>
            </>
            }
        </div>
    );
};

export default mobxReact.observer(injectIntl(DateMultiSelectionDropdown));
